import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRememberedSearchParams } from './useRememberedSearchParams';
import { format, parse } from 'date-fns';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { documentService } from '@services/document.service';
import { TABLE_DEFAULT_PAGE_SIZE } from '@constants/table';

/** Kept as a named re-export: callers and tests refer to it by this name. */
export const DOCUMENTS_PAGE_SIZE = TABLE_DEFAULT_PAGE_SIZE;

/** Debounce before a documents search hits the server. */
const DOCUMENTS_SEARCH_DEBOUNCE_MS = 300;

export const DOCUMENT_SORT_FIELDS = ['date', 'name', 'size', 'category'] as const;
export type DocumentSortBy = (typeof DOCUMENT_SORT_FIELDS)[number];

/** Mirrors the route's own `le=` so the UI never asks for a page it will refuse. */
export const MAX_DOCUMENTS_PAGE = 100000;
export type SortOrder = 'asc' | 'desc';

export interface DocumentFilters {
  categories: string[];
  tags: string[];
  dateRange: { start: Date | null; end: Date | null };
  uploadedBy: string[];
  fileTypes: string[];
}

export const EMPTY_FILTERS: DocumentFilters = {
  categories: [],
  tags: [],
  dateRange: { start: null, end: null },
  uploadedBy: [],
  fileTypes: [],
};

export const countActiveFilters = (filters: DocumentFilters): number =>
  filters.categories.length +
  filters.tags.length +
  filters.uploadedBy.length +
  filters.fileTypes.length +
  (filters.dateRange.start || filters.dateRange.end ? 1 : 0);

export const transformDocument = (doc: any) => ({
  id: doc.id,
  title: doc.title || doc.fileName || '',
  fileName: doc.fileName || '',
  description: doc.description || '',
  summary: doc.summary || '',
  category: doc.category,
  ...(doc.categoryName != null ? { categoryName: doc.categoryName as string } : {}),
  tags: doc.tags || [],
  uploadedBy: doc.createdByName || doc.createdBy || doc.currentVersion?.uploadedBy || 'Unknown',
  uploadedAt: doc.uploadedAt || doc.currentVersion?.uploadedAt || doc.createdAt,
  fileType:
    doc.mimeType?.split('/').pop() || doc.currentVersion?.mimeType?.split('/').pop() || 'unknown',
  size: doc.fileSize || doc.currentVersion?.fileSize || 0,
  version: doc.currentVersion?.versionNumber || doc.version || 1,
  thumbnail: null,
  analysisHistory: doc.analysis_history || [],
  // The list endpoint sends a count rather than the entries, whose content is
  // the full deep-analysis text. Detail payloads carry the array instead.
  analysisCount: doc.analysisCount ?? doc.analysis_history?.length ?? 0,
  createdBy: doc.createdBy || doc.currentVersion?.uploadedBy || '',
  canDelete: Boolean(doc.canDelete),
});

/** The UI-facing shape the list renders — the output of transformDocument. */
export type DocumentListItem = ReturnType<typeof transformDocument>;

interface UseDocumentsQuery {
  searchQuery: string;
  /** Typed term the server has not yet answered for; '' once its rows have landed. */
  pendingSearch: string;
  setSearchQuery: (value: string) => void;
  filters: DocumentFilters;
  setFilters: (filters: DocumentFilters) => void;
  sortBy: DocumentSortBy;
  sortOrder: SortOrder;
  /** Same field toggles direction; a new field resets to descending. */
  changeSort: (field: DocumentSortBy) => void;
  documents: DocumentListItem[];
  totalCount: number;
  loading: boolean;
  error: { code: string } | null;
  reload: () => void;
  /** 1-based, matching the API. The table's pageIndex is this minus one. */
  page: number;
  pageSize: number;
  goToPage: (page: number) => void;
  setPageSize: (size: number) => void;
  removeDocument: (id: string) => Promise<void>;
  updateTags: (id: string, tags: string[]) => Promise<void>;
}

/** Local calendar date: toISOString() would shift a late-evening date a day back. */
export const formatIsoDate = (value: Date | null): string | undefined =>
  value ? format(value, 'yyyy-MM-dd') : undefined;

export const parseIsoDate = (value: string): Date | null => {
  const parsed = parse(value, 'yyyy-MM-dd', new Date());
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export function useDocumentsQuery(): UseDocumentsQuery {
  // Sort, page and date live in the URL so reload and browser back/forward keep
  // them; the rest stays local. Copy `current` rather than mutating it, per
  // CADThicknessWorkspace — a mutated URLSearchParams desyncs the render.
  const [searchParams, setSearchParams] = useRememberedSearchParams('documents.filters');
  const writeParams = useCallback(
    (patch: Record<string, string | undefined>, options?: { replace?: boolean }) => {
      setSearchParams((current: URLSearchParams) => {
        const next = new URLSearchParams(current);
        Object.entries(patch).forEach(([key, value]) => {
          if (value === undefined || value === '') next.delete(key);
          else next.set(key, value);
        });
        return next;
      }, options);
    },
    [setSearchParams],
  );

  // The URL is attacker-influenced: a shared link must not put arbitrary values on the wire.
  const rawSort = searchParams.get('sort');
  const sortBy: DocumentSortBy = DOCUMENT_SORT_FIELDS.includes(rawSort as DocumentSortBy)
    ? (rawSort as DocumentSortBy)
    : 'date';
  const sortOrder: SortOrder = searchParams.get('dir') === 'asc' ? 'asc' : 'desc';
  const rawPage = Number(searchParams.get('page'));
  const page = Number.isSafeInteger(rawPage) && rawPage >= 1
    ? Math.min(rawPage, MAX_DOCUMENTS_PAGE)
    : 1;
  const dateFromParam = searchParams.get('from') ?? undefined;
  const dateToParam = searchParams.get('to') ?? undefined;

  const [searchQuery, setSearchQuery] = useState('');
  const [localFilters, setFilters] = useState<DocumentFilters>(EMPTY_FILTERS);

  // The URL owns the range; the rest of the filter object stays local.
  const filters = useMemo<DocumentFilters>(
    () => ({
      ...localFilters,
      dateRange: {
        start: dateFromParam ? parseIsoDate(dateFromParam) : null,
        end: dateToParam ? parseIsoDate(dateToParam) : null,
      },
    }),
    [localFilters, dateFromParam, dateToParam],
  );

  // react-router 7.18's setSearchParams depends on searchParams, so writeParams is a new
  // function after every URL change; read it through a ref so the fetch effect does not
  // re-run for a URL change that alters none of its inputs.
  const writeParamsRef = useRef(writeParams);
  writeParamsRef.current = writeParams;

  const [documents, setDocuments] = useState<DocumentListItem[]>([]);
  const [fetchedSearch, setFetchedSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSizeState] = useState(DOCUMENTS_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ code: string } | null>(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const reload = useCallback(() => setReloadVersion((version) => version + 1), []);

  // Views narrow by `pendingSearch` only until the server answers for the term — its
  // predicate also matches file name and category, which the client cannot see.
  const debouncedSearch = useDebouncedValue(searchQuery, DOCUMENTS_SEARCH_DEBOUNCE_MS);

  const queryFor = useCallback(
    (requested: number) => ({
      page: requested,
      pageSize,
      category: filters.categories.length > 0 ? filters.categories[0] : undefined,
      tags: filters.tags,
      search: debouncedSearch || undefined,
      fileType: filters.fileTypes.length > 0 ? filters.fileTypes.join(',') : undefined,
      uploadedBy: filters.uploadedBy.length > 0 ? filters.uploadedBy.join(',') : undefined,
      dateFrom: formatIsoDate(filters.dateRange.start),
      dateTo: formatIsoDate(filters.dateRange.end),
      sortBy,
      sortOrder,
    }),
    [filters, debouncedSearch, sortBy, sortOrder, pageSize],
  );

  useEffect(() => {
    let cancelled = false;

    const loadPage = async () => {
      setLoading(true);

      try {
        const query = queryFor(page);
        const response = await documentService.getDocuments(query);
        if (cancelled) return;

        const lastPage = response.totalPages
          ?? Math.max(1, Math.ceil((response.total ?? 0) / pageSize));
        if (page > lastPage && (response.total ?? 0) > 0) {
          // Land the reader on real rows rather than an empty page they cannot
          // explain; replace so Back still leaves the list.
          writeParamsRef.current({ page: lastPage > 1 ? String(lastPage) : undefined }, { replace: true });
          return;
        }

        setError(null);
        setDocuments(response.results.map(transformDocument));
        setTotalCount(response.total);
        setFetchedSearch(query.search ?? '');
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load documents:', error);
        const code = (error as { code?: unknown } | null)?.code;
        setError({ code: typeof code === 'string' ? code : error instanceof TypeError ? 'ERR_NETWORK' : 'UNKNOWN' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPage();
    return () => {
      cancelled = true;
    };
    // queryFor now carries search and every filter to the server, so a change
    // to any of them flows through it and refetches the correctly-filtered page.
  }, [queryFor, page, pageSize, reloadVersion]);

  const goToPage = useCallback((next: number) => {
    writeParams({ page: next > 1 ? String(next) : undefined });
  }, [writeParams]);

  // react-router commits the URL write as a transition; a plain setState next to it
  // commits first and fetches once with the new size but the old page.
  const setPageSize = useCallback((size: number) => {
    startTransition(() => {
      setPageSizeState(size);
      writeParams({ page: undefined });
    });
  }, [writeParams]);

  // Anything that changes which rows exist sends the reader back to page 1 in
  // the same event: page 4 of the old result set is meaningless against the
  // new one, and the API answers an out-of-range page with nothing at all.
  const changeSort = useCallback(
    (field: DocumentSortBy) => {
      if (field === sortBy) {
        writeParams({ dir: sortOrder === 'asc' ? 'desc' : 'asc', page: undefined });
        return;
      }
      writeParams({ sort: field, dir: 'desc', page: undefined });
    },
    [sortBy, sortOrder, writeParams],
  );

  const applyFilters = useCallback((next: DocumentFilters) => {
    startTransition(() => {
      setFilters(next);
      writeParams({
        from: formatIsoDate(next.dateRange.start),
        to: formatIsoDate(next.dateRange.end),
        page: undefined,
      });
    });
  }, [writeParams]);

  const applySearchQuery = useCallback((value: string) => {
    setSearchQuery(value);
    writeParams({ page: undefined }, { replace: true });
  }, [writeParams]);

  const removeDocument = useCallback(async (id: string) => {
    try {
      await documentService.deleteDocument(id);
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));
      setTotalCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to delete document:', error);
      // The page owns the user-facing notification. Re-throw so a rejected
      // delete is not reported as successful after this hook logs it.
      throw error;
    }
  }, []);

  const updateTags = useCallback(async (id: string, tags: string[]) => {
    try {
      await documentService.updateDocumentTags(id, tags);
      setDocuments((prev) => prev.map((doc) => (doc.id === id ? { ...doc, tags } : doc)));
    } catch (error) {
      console.error('Failed to update tags:', error);
    }
  }, []);

  return {
    searchQuery,
    pendingSearch: searchQuery !== fetchedSearch ? searchQuery : '',
    setSearchQuery: applySearchQuery,
    filters,
    setFilters: applyFilters,
    sortBy,
    sortOrder,
    changeSort,
    documents,
    totalCount,
    loading,
    error,
    reload,
    page,
    pageSize,
    goToPage,
    setPageSize,
    removeDocument,
    updateTags,
  };
}
